import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs';
import { IOrderUser } from '../../models/order.model';
import { OrdersService } from '../../services/orders.services';
import { CommonService } from 'src/app/shared/common.service';
import { userInitials } from 'src/app/shared/utils/order-users-dedupe';
import { getUserColor } from 'src/app/shared/utils/user-color.util';

// ─────────────────────────────────────────────────────────────────────────────
// OrderOwnerPopoverComponent
// ─────────────────────────────────────────────────────────────────────────────
// Inline owner editor for the orders table. Exposes a <mat-menu> via @ViewChild
// so the parent can wire `[matMenuTriggerFor]="ownerPopover.menu"` on the owner
// cell. Reuses OrdersService (searchBackofficeUsers + updateOrderOwner) — no new
// backend. On a successful change it emits the new owner so the parent can update
// the row locally.
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-order-owner-popover',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatMenuModule, MatProgressSpinnerModule, TranslateModule],
  templateUrl: './order-owner-popover.component.html',
  styleUrls: ['./order-owner-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderOwnerPopoverComponent implements OnInit, OnDestroy {
  /** The order whose owner this popover edits. */
  @Input() order!: { id: number; owner?: IOrderUser | null };

  /** Emits the new owner after a successful change. */
  @Output() ownerChanged = new EventEmitter<IOrderUser>();

  /** Exposed so the parent can bind [matMenuTriggerFor]. */
  @ViewChild('ownerMenu', { static: true }) menu!: MatMenu;

  @ViewChild('searchInput') private searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChildren('optionBtn') private optionButtons?: QueryList<ElementRef<HTMLButtonElement>>;

  searchControl = new FormControl<string>('', { nonNullable: true });
  options: IOrderUser[] = [];
  loadingOptions = false;
  savingId: number | null = null;

  readonly userInitials = userInitials;
  readonly getUserColor = getUserColor;

  private search$ = new Subject<string>();
  private sub?: Subscription;

  constructor(
    private ordersService: OrdersService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sub = this.search$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((q) => {
          this.loadingOptions = true;
          this.cdr.markForCheck();
          const request$ = q
            ? this.ordersService.searchBackofficeUsers(q, 20)
            : this.ordersService.listBackofficeUsers(20);
          return request$.pipe(
            finalize(() => {
              this.loadingOptions = false;
              this.cdr.markForCheck();
            })
          );
        })
      )
      .subscribe((users) => {
        this.options = users;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Called by the parent on (menuOpened): reset and load the initial list. */
  onOpen(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.savingId = null;
    this.search$.next('');
  }

  onSearchInput(value: string): void {
    this.search$.next(value?.trim() ?? '');
  }

  /** Move focus to the option at `index` (clamped); negative goes back to the search input. */
  focusOption(index: number): void {
    if (index < 0) {
      this.focusSearch();
      return;
    }
    const buttons = this.optionButtons?.toArray() ?? [];
    if (!buttons.length) {
      return;
    }
    buttons[Math.min(index, buttons.length - 1)]?.nativeElement.focus();
  }

  focusSearch(): void {
    this.searchInputRef?.nativeElement.focus();
  }

  /** Enter from the search input confirms the top result. */
  selectFirstOption(): void {
    if (this.options.length) {
      this.selectUser(this.options[0]);
    }
  }

  selectUser(user: IOrderUser): void {
    if (!user?.id || user.id === this.order.owner?.id || this.savingId != null) {
      return;
    }
    this.savingId = user.id;
    this.cdr.markForCheck();
    this.ordersService
      .assignOrderOwner(this.order.id, user.id)
      .pipe(finalize(() => { this.savingId = null; this.cdr.markForCheck(); }))
      .subscribe({
        next: () => {
          // The clicked user is the new owner — emit it directly so the row
          // updates without depending on the PATCH response body shape.
          this.ownerChanged.emit(user);
          this.commonService.showToast(
            this.translateService.instant('quotations_and_orders.ownership.change_owner'),
            'SUCCESS'
          );
        },
        error: () => {},
      });
  }
}
